/**
 * ACCOUNT CREATION — the sequence, and it is a sequence because every step feeds the next.
 *
 * This file is the missing half of `auth/turnkey.ts`: that file has had `createSessionKey`, `sealOtpCode`,
 * `buildLoginSignature` and `storeUserSession` for its whole life with **no call site**, because the four
 * routes they talk to did not exist on the server. They do now (`apps/api/src/user/routes/onboarding.ts`),
 * and this is what calls them in order.
 *
 * ── TWO CALLS, NOT ONE, AND THE SESSION KEY SPANS THEM ────────────────────────────────────────────────
 *
 *   begin(email)          → sends the code, and RETURNS A HANDLE holding `otpId`, `targetBundle` and the
 *                           session PUBLIC key. The private half is already in the SDK's keystore.
 *   complete(handle, code)→ seals the code to that handle, verifies, provisions, logs in, stores the session
 *                           and remembers the wallet.
 *
 * They are split because a human types the code in between, and fusing them would mean either blocking on
 * user input inside one promise or generating the session key twice. **The key MUST be the same across both**:
 * it is what the sealed bundle is bound to and what the vendor expects to have signed the login message, so a
 * second key produces a `verificationToken` bound to a keypair this browser is not using — which fails at the
 * login signature with a message about the signature rather than about the key.
 *
 * ── WHY THE ORDER IS THIS ORDER ───────────────────────────────────────────────────────────────────────
 *
 * The wallet is provisioned AFTER the code is verified, never before. Creating a sub-organisation for an
 * unproven email is harmless in isolation — an empty wallet nobody can sign for — but it lets anyone mint
 * sub-orgs for addresses they do not own, which squats a real user's email. The server enforces nothing about
 * this ordering, so the client not doing it is the control.
 *
 * ── THE STEP THAT IS EASIEST TO OMIT AND HARDEST TO DEBUG ─────────────────────────────────────────────
 *
 * `storeUserSession`. Until the session string is handed to the SDK it is a value we were given and threw
 * away. Everything keeps working — the wallet exists, the address renders, `/session` authenticates — right
 * up until the first signature, which fails at the Buy button looking like a different bug. It is called here
 * before anything is written to the wallet record, so a stored record always implies a stored session.
 */

import { findOrCreateWallet, otpLogin, startEmailOtp, verifyEmailOtp } from "../api/trade.ts";
import {
  assertSecureContext,
  buildLoginSignature,
  createSessionKey,
  sealOtpCode,
  storeUserSession,
} from "./turnkey.ts";
import { rememberWallet } from "./wallet.ts";

/** What `begin` hands to `complete`. Opaque to the UI, which should hold it and not read it. */
export interface OtpHandle {
  readonly email: string;
  readonly otpId: string;
  readonly targetBundle: string;
  /** The PUBLIC half. The private half never leaves the SDK's keystore, and this type is shaped so there is
   *  nowhere to put it even by accident. */
  readonly sessionPublicKey: string;
}

export interface OnboardResult {
  readonly address: string;
  readonly subOrgId: string;
  /** `false` for a returning user. The UI can say "welcome back" instead of "wallet created". */
  readonly created: boolean;
}

/**
 * STEP 1 — create the browser's session key, then ask the server to email a code.
 *
 * The key is created FIRST and deliberately: it must exist before the code arrives, because the code is
 * sealed to it. Creating it after would mean sealing to a key the verification token was not bound to.
 *
 * `assertSecureContext` runs before anything else so the failure names the ORIGIN. Over plain http
 * `crypto.subtle` is undefined and every key operation fails as the SDK's generic "Failed to create API key
 * pair", which sends whoever is debugging it into the signing code instead of at the URL bar.
 */
export async function begin(email: string): Promise<OtpHandle> {
  assertSecureContext();
  const sessionPublicKey = await createSessionKey();
  const { otpId, targetBundle } = await startEmailOtp(email);
  // A missing bundle is fatal HERE rather than at seal time: sealing to an empty string produces a bundle the
  // vendor rejects, one step later, with an error about the code.
  if (!targetBundle) {
    throw new Error("the signing vendor did not issue an encryption key for this code — try again");
  }
  return { email, otpId, targetBundle, sessionPublicKey };
}

/**
 * STEP 2 — seal the code, prove the mailbox, provision the wallet, and take a signing session.
 *
 * Four network calls in a fixed order. The first failure aborts: there is nothing to roll back, because
 * nothing before `login` has issued a credential and `findOrCreateWallet` is idempotent — so a user who fails
 * at login and retries the whole flow gets the SAME wallet rather than a second one.
 */
export async function complete(handle: OtpHandle, code: string): Promise<OnboardResult> {
  // Sealed in the browser, to the vendor's ephemeral key, bound to our session key. The plaintext never
  // touches the network and never reaches our server.
  const encryptedOtpBundle = await sealOtpCode(code, handle.targetBundle, handle.sessionPublicKey);

  const { verificationToken } = await verifyEmailOtp({ otpId: handle.otpId, encryptedOtpBundle });

  // AFTER the mailbox proof — see the header.
  const wallet = await findOrCreateWallet(handle.email);

  // The signature is built from the TOKEN, not from our session key: the helper decodes the bound key out of
  // the verification token, and that is the key the vendor expects to have signed. Passing ours makes the
  // server reject it.
  const clientSignature = await buildLoginSignature(verificationToken, handle.sessionPublicKey);

  const session = await otpLogin({
    email: handle.email,
    verificationToken,
    publicKey: handle.sessionPublicKey,
    clientSignature,
  });

  // ⚠️ BEFORE THE WALLET RECORD IS WRITTEN. A record without a stored session is a browser that renders as
  // having a wallet and cannot sign for it — the state the whole sign-in dialog exists to avoid claiming.
  await storeUserSession(session.session);

  const address = session.address ?? wallet.address;
  if (!address) {
    // Reachable if the organisation genuinely carries no Solana account. Better as a named failure than as a
    // wallet record with a null address, which every downstream reader would have to defend against.
    throw new Error("your wallet was created but has no Solana address yet — contact support");
  }
  const subOrgId = session.subOrganizationId || wallet.subOrganizationId;

  rememberWallet({ email: handle.email, subOrgId, address });
  return { address, subOrgId, created: wallet.created };
}
